import React, { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert,
} from "@mui/material";
import { Video, Settings } from "lucide-react";
import { useCreateVideoRoom } from "../../services/video";

interface VideoCallInitiatorProps {
  onVideoRoomCreated: (roomRef: string) => void;
  onCancel: () => void;
}

const VideoCallInitiator: React.FC<VideoCallInitiatorProps> = ({
  onVideoRoomCreated,
  onCancel,
}) => {
  const [recordingEnabled, setRecordingEnabled] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createVideoRoom = useCreateVideoRoom();

  const handleCreateRoom = async () => {
    setIsCreating(true);
    setError(null);

    const response = await createVideoRoom.request({
      recordingEnabled,
      familyRef: "",
    });

    if (response && response.videoRoom) {
      onVideoRoomCreated(response.videoRoom.uuid);
    } else {
      setError("Failed to create video room");
    }
    setIsCreating(false);
  };

  return (
    <Dialog open={true} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Video size={24} />
          <Typography variant="h6">Start Video Call</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 2 }}>
          <Typography
            variant="subtitle1"
            sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}
          >
            <Settings size={20} />
            Call Settings
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={recordingEnabled}
                onChange={(e) => setRecordingEnabled(e.target.checked)}
                color="primary"
              />
            }
            label="Enable transcriptions"
          />
        </Box>

        <Box sx={{}}>
          <Typography variant="body2" color="text.secondary">
            The video call will start immediately once created. You can share
            the call link with participants to join.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel} disabled={isCreating}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={
            isCreating ? <CircularProgress size={20} /> : <Video size={20} />
          }
          onClick={handleCreateRoom}
          disabled={isCreating}
        >
          {isCreating ? "Creating..." : "Start Video Call"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VideoCallInitiator;
